import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

/**
 * Hook for checking admin status and permissions
 * SECURITY NOTE: This is for UI display only - all security is enforced server-side
 */
export const useAdminPermissions = () => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isLoaded) {
        return;
      }

      if (!isSignedIn) {
        setIsAdmin(false);
        setPermissions([]);
        setLoading(false);
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          setIsAdmin(false);
          setPermissions([]);
          setLoading(false);
          return;
        }

        const response = await fetch('https://parrotmate.com/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setIsAdmin(userData.is_admin || false);
          setPermissions(userData.admin_permissions || []);
        } else {
          setIsAdmin(false);
          setPermissions([]);
        }
      } catch (err) {
        console.error('Failed to check admin status:', err);
        setError(err.message);
        setIsAdmin(false);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [isLoaded, isSignedIn, getToken]);

  /**
   * Check if user has a specific permission (for UI display only)
   * @param {string} permission - Permission to check
   * @returns {boolean} - Whether user has the permission
   */
  const hasPermission = useCallback((permission) => {
    if (!isAdmin) return false;
    if (!Array.isArray(permissions)) return isAdmin; // Fallback to isAdmin if permissions is not an array
    return permissions.includes(permission) || permissions.includes('*');
  }, [isAdmin, permissions]);

  /**
   * Check if user has any of the specified permissions (for UI display only)
   * @param {string[]} permissionList - Array of permissions to check
   * @returns {boolean} - Whether user has any of the permissions
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (!isAdmin) return false;
    if (!Array.isArray(permissionList)) return isAdmin;
    return permissionList.some(permission => hasPermission(permission));
  }, [isAdmin, hasPermission]);

  /**
   * Check if user has all of the specified permissions (for UI display only)
   * @param {string[]} permissionList - Array of permissions to check
   * @returns {boolean} - Whether user has all of the permissions
   */
  const hasAllPermissions = useCallback((permissionList) => {
    if (!isAdmin) return false;
    if (!Array.isArray(permissionList)) return isAdmin;
    return permissionList.every(permission => hasPermission(permission));
  }, [isAdmin, hasPermission]);

  return {
    isAdmin,
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };
};

export default useAdminPermissions;
